import type { CSSProperties } from 'react'
import { Position } from 'reactflow'
import type { HandleConfig } from './types'

type PortSchema = {
  id: string
}

type NodeSchemaWithPorts = {
  inputs?: PortSchema[]
  outputs?: PortSchema[]
}

type SchemaHandleDefaults = {
  position?: Position
  style?: CSSProperties
}

type SchemaHandleOverride = SchemaHandleDefaults & {
  disabled?: boolean
}

export type SchemaHandleOptions = {
  inputDefaults?: SchemaHandleDefaults
  outputDefaults?: SchemaHandleDefaults
  inputs?: Record<string, SchemaHandleOverride>
  outputs?: Record<string, SchemaHandleOverride>
}

function mergeStyle(
  defaults?: CSSProperties,
  override?: CSSProperties,
): CSSProperties | undefined {
  if (!defaults && !override) return undefined
  return { ...defaults, ...override }
}

function createPortHandles(
  ports: PortSchema[] | undefined,
  type: HandleConfig['type'],
  fallbackPosition: Position,
  defaults?: SchemaHandleDefaults,
  overrides?: Record<string, SchemaHandleOverride>,
): HandleConfig[] {
  return (ports ?? []).flatMap((port) => {
    const override = overrides?.[port.id]
    if (override?.disabled) return []

    return [{
      id: port.id,
      type,
      position: override?.position ?? defaults?.position ?? fallbackPosition,
      style: mergeStyle(defaults?.style, override?.style),
    }]
  })
}

export function createHandlesFromSchema(
  schema: NodeSchemaWithPorts,
  options: SchemaHandleOptions = {},
): HandleConfig[] {
  return [
    ...createPortHandles(
      schema.inputs,
      'target',
      Position.Left,
      options.inputDefaults,
      options.inputs,
    ),
    ...createPortHandles(
      schema.outputs,
      'source',
      Position.Right,
      options.outputDefaults,
      options.outputs,
    ),
  ]
}
